import type {EncodedConvertedNode, Format, MapsNodes, Resolution, NormalY} from "#material-maps-exporter/core/types"

// Domain vocabulary

const metalnessRoughnessMaps = ["diffuse", "metalness", "roughness", "specular"] as const
const specularGlossinessMaps = ["diffuse", "reflect", "gloss", "fresnel-ior"] as const
const singleAnisotropyMaps = ["anisotropy"] as const
const splitAnisotropyMaps = ["anisotropy-strength", "anisotropy-rotation"] as const

export const TEXTURE_TYPES_FOR_ENGINE = {
    metalnessRoughness: {
        cm: [...metalnessRoughnessMaps, "normal", ...singleAnisotropyMaps, "displacement", "transmission", "alpha"] as const,
        corona: [...metalnessRoughnessMaps, "normal", ...splitAnisotropyMaps, "displacement", "transmission", "alpha"] as const,
        vray: [...metalnessRoughnessMaps, "normal", ...splitAnisotropyMaps, "displacement", "transmission", "alpha"] as const,
        cycles: [...metalnessRoughnessMaps, "normal", ...splitAnisotropyMaps, "displacement", "transmission", "alpha"] as const,
    },
    specularGlossiness: {
        cm: [...specularGlossinessMaps, "normal", "anisotropy", "displacement", "transmission", "alpha"] as const,
        corona: [...specularGlossinessMaps, "normal", ...splitAnisotropyMaps, "displacement", "transmission", "alpha"] as const,
        vray: [...specularGlossinessMaps, "normal", ...splitAnisotropyMaps, "displacement", "transmission", "alpha"] as const,
        cycles: [...specularGlossinessMaps, "normal", ...splitAnisotropyMaps, "displacement", "transmission", "alpha"] as const,
    },
} as const

export const engines = ["cm", "corona", "vray", "cycles"] as const
export type Engine = (typeof engines)[number]

export const workflows = ["metalnessRoughness", "specularGlossiness"] as const
export type Workflow = (typeof workflows)[number]

export type TextureType = (typeof TEXTURE_TYPES_FOR_ENGINE)[Workflow][keyof (typeof TEXTURE_TYPES_FOR_ENGINE)[Workflow]] extends readonly (infer T)[]
    ? T
    : never

// Config data model

/** What one export is: the five settings a conversion is configured by, and the whole of a custom export's identity. */
export type Settings = {
    workflow: Workflow
    engine: Engine
    format: Format
    normalY: NormalY
    resolution: Resolution
}

/**
 * One map of an export and the filename it is written under inside the zip. The filename is kept
 * rather than re-derived because it is schema-driven: a schema edit would make a recomputation
 * disagree with what the zip actually holds.
 *
 * `mapType` is a `TextureType` when written; it is typed as a string because stored rows are a record
 * of what a zip holds, and refusing a whole config over a map name the vocabulary has since dropped
 * would hide an export that exists.
 */
export type ConvertedMap = {
    mapType: string
    filename: string
}

/** Identity and version in one field: a detached blob stays recognisable, and an unknown future version is refused rather than misread. */
export const PBR_EXPORT_CONFIG_SCHEMA = "pbrExportConfig/0.0.2"

/**
 * A record of how an export was configured, written once when the export is created and read back
 * only through `parseStoredPbrExportConfig`, which also lifts the nested encoding older rows were
 * written in. Nothing writes to a config after it is created: the result lives on
 * `MaterialOutput.result`, and the revision on `MaterialOutput.materialRevision`.
 */
export type Config = Settings & {
    schema: typeof PBR_EXPORT_CONFIG_SCHEMA
    /** The export's folder name and the zip's fallback name — a default is named after its format and resolution, a custom export after the material. */
    name: string
    maps: ConvertedMap[]
    /** The info file as it is written into the zip. */
    info: {name: string; content: string}
}

/** How an export's `info` file is written. A vocabulary rather than a shape, so naming can read it without taking the request. */
export const infoFileFormats = ["json", "text"] as const
export type InfoFileFormat = (typeof infoFileFormats)[number]

/** The `info` file an export carries beside its conversion. It describes that one conversion, so it does not name it. */
export type InfoRequest = {
    name: string
    format: InfoFileFormat
}

/**
 * What an export is asked to be: one folder, holding one conversion — described by its settings — and
 * its info file. That is the only shape an export has ever had, and the only one
 * `@cm/material-outputs-filenames` can name.
 *
 * Never persisted — `exportConfigForExportRequest` turns it into the `Config` that is — so it has no
 * legacy encoding to stay compatible with, unlike `Config`, whose nested tree was that encoding.
 */
export type Request = {
    name: string
    settings: Settings
    info: InfoRequest
}

type ArrayElement<A> = A extends readonly (infer T)[] ? T : never
export const SRGB_TEXTURE_TYPES_FOR_ENGINE: {
    [workflow in Workflow]: {[engine in Engine]: ArrayElement<(typeof TEXTURE_TYPES_FOR_ENGINE)[workflow][engine]>[]}
} = {
    metalnessRoughness: {
        cm: ["diffuse"],
        corona: ["diffuse"],
        vray: ["diffuse"],
        cycles: ["diffuse"],
    },
    specularGlossiness: {
        cm: ["diffuse", "reflect"],
        corona: ["diffuse", "reflect"],
        vray: ["diffuse", "reflect"],
        cycles: ["diffuse", "reflect"],
    },
}

export type ConvertResult<E extends Engine> =
    | MapsNodes<(typeof TEXTURE_TYPES_FOR_ENGINE)["metalnessRoughness"][E], EncodedConvertedNode>
    | MapsNodes<(typeof TEXTURE_TYPES_FOR_ENGINE)["specularGlossiness"][E], EncodedConvertedNode>

// Output types

// Named by `MaterialOutputType`'s string values: the enum is generated code, which this package must not pull into its dependency graph.
export const PBR_EXPORT_OUTPUT_TYPES = [
    "PbrExportCustom",
    "PbrExportDefaultExrOriginal",
    "PbrExportDefaultJpegLow",
    "PbrExportDefaultJpegOriginal",
    "PbrExportDefaultTiffLow",
    "PbrExportDefaultTiffOriginal",
] as const

export type PbrExportOutputType = (typeof PBR_EXPORT_OUTPUT_TYPES)[number]

export type PbrExportDefaultOutputType = Exclude<PbrExportOutputType, "PbrExportCustom">

export const isPbrExportMaterialOutputType = (type: string): type is PbrExportOutputType => (PBR_EXPORT_OUTPUT_TYPES as readonly string[]).includes(type)

// A `Record` so a new default type cannot be added without its pair; declaration order is the order the outputs UI renders.
export const DEFAULT_PBR_EXPORT_CONVERSIONS: Record<PbrExportDefaultOutputType, {format: Format; resolution: Resolution}> = {
    PbrExportDefaultJpegLow: {format: "jpeg", resolution: "dpi72"},
    PbrExportDefaultJpegOriginal: {format: "jpeg", resolution: "original"},
    PbrExportDefaultTiffLow: {format: "tiff", resolution: "dpi72"},
    PbrExportDefaultTiffOriginal: {format: "tiff", resolution: "original"},
    PbrExportDefaultExrOriginal: {format: "exr", resolution: "original"},
}
